import { useState, useMemo, useCallback, memo } from "react"
import styles from "./PortfolioCard.module.scss"
import { connect } from "react-redux"
import { useNavigate } from "react-router-dom"
import useUserImage from "../../../../hooks/useUserImage"
import { ReactComponent as NoImage } from "../../../../assets/svg/noImageIcon.svg"
import { ReactComponent as MundoPost } from "../../../../assets/svg/worldPost.svg"
import { ReactComponent as DotPost } from "../../../../assets/svg/dotPost.svg"
import { ReactComponent as ProductIcon } from "../../../../assets/svg/ProductIcon.svg"
import { ReactComponent as ServiceIcon } from "../../../../assets/svg/ServiceIcon.svg"
import { MapPin, Calendar, Eye, ImageIcon, Plus } from "lucide-react"
import PortfolioItemAttachment from "../../../MainInfoItem/PortfolioItem/PortfolioItemAttachment"

const ImageWithBlurredBackground = memo(({ src, alt, onClick, className }) => {
  const encodedSrc = useMemo(() => {
    return src ? encodeURI(src) : "/placeholder.svg"
  }, [src])

  return (
    <div className={styles.portfolio_image_container}>
      <div className={styles.portfolio_image_background} style={{ backgroundImage: `url(${encodedSrc})` }} />
      <img src={src || "/placeholder.svg"} alt={alt} className={className} onClick={onClick} />
    </div>
  )
})

ImageWithBlurredBackground.displayName = "ImageWithBlurredBackground"

const PortfolioCard = ({ userData, item, ...props }) => {
  const [isHovered, setIsHovered] = useState(false)
  const [showPortfolioDetail, setShowPortfolioDetail] = useState(false)
  const navigate = useNavigate()

  const {
    id,
    name,
    description,
    startDate,
    endDate,
    zone,
    type,
    price,
    priceUnit,
    currencyType,
    portfolio_images,
    createdAt,
    user,
  } = item

  const { name: userName, last_name, razon_social, type: userType, id: userId } = user || {}
  
  const completeName = useMemo(() => {
    return userType === "COMPANY" ? razon_social : `${userName} ${last_name}`
  }, [userType, razon_social, userName, last_name])
  
  const { imageError, initials, profileImage } = useUserImage(user)

  const hasImages = useMemo(() => {
    return portfolio_images && portfolio_images.length > 0
  }, [portfolio_images])

  const imageCount = useMemo(() => {
    return hasImages ? portfolio_images.length : 0
  }, [hasImages, portfolio_images])

  const imageUrls = useMemo(() => {
    return portfolio_images?.map(img => img?.image) || []
  }, [portfolio_images])

  const handleImageClick = useCallback((index) => (e) => {
    e.stopPropagation()
    setShowPortfolioDetail(true)
  }, [])

  const handleProfile = useCallback((id, completeName) => {
    navigate(`/perfil/${id}`)
  }, [navigate])

  const tiempoTranscurrido = useMemo(() => {
    if (!createdAt) return ""
    const fechaActual = new Date()
    const fechaCreacionPost = new Date(createdAt)
    const diferencia = fechaActual - fechaCreacionPost

    const minutos = Math.floor(diferencia / (1000 * 60))
    const horas = Math.floor(diferencia / (1000 * 60 * 60))
    const dias = Math.floor(diferencia / (1000 * 60 * 60 * 24))
    const semanas = Math.floor(dias / 7)
    const meses = Math.floor(dias / 30)
    const años = Math.floor(dias / 365)

    if (minutos === 0) return "Ahora"
    if (minutos === 1) return "1 minuto"
    if (minutos < 60) return `${minutos} minutos`
    if (horas === 1) return "1 hora"
    if (horas < 24) return `${horas} horas`
    if (dias === 1) return "1 día"
    if (dias < 7) return `${dias} días`
    if (semanas === 1) return "1 semana"
    if (semanas < 4) return `${semanas} semanas`
    if (meses === 1) return "1 mes"
    if (meses < 12) return `${meses} meses`
    if (años === 1) return "1 año"
    return `${años} años`
  }, [createdAt])

  const precioFormateado = useMemo(() => {
    if (!price) return null
    const currency = currencyType === "USD" ? "US$" : "$"
    const unit = priceUnit ? `/${priceUnit}` : ""

    const formattedNumber = Math.floor(price).toLocaleString("es-AR")

    return `${currency} ${formattedNumber}${unit}`
  }, [price, currencyType, priceUnit])

  const fechaInicioFormateada = useMemo(() => {
    if (!startDate) return null
    const date = new Date(startDate)
    return date.toLocaleDateString("es-ES", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    })
  }, [startDate])

  const fechaFinFormateada = useMemo(() => {
    if (!endDate) return null
    const date = new Date(endDate)
    return date.toLocaleDateString("es-ES", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    })
  }, [endDate])

  const handleCardClick = useCallback(() => {
    setShowPortfolioDetail(true)
  }, [])

  const renderGallery = () => {
    if (!hasImages) {
      return (
        <div className={styles.portfolio_mock}>
          <div className={styles.portfolio_mock_container}>
            <div className={styles.portfolio_mock_icon}>
              <ImageIcon size={48} />
            </div>
            <div className={styles.portfolio_mock_pattern}>
              <div className={styles.portfolio_mock_pattern_item}></div>
              <div className={styles.portfolio_mock_pattern_item}></div>
              <div className={styles.portfolio_mock_pattern_item}></div>
              <div className={styles.portfolio_mock_pattern_item}></div>
            </div>
            <div className={styles.portfolio_mock_text}>
              <span>{type === "producto" ? "Producto" : "Servicio"}</span>
            </div>
          </div>
        </div>
      )
    }

    const galleryClass = imageCount === 1 
      ? styles.portfolio_gallery_single 
      : imageCount === 2 
      ? styles.portfolio_gallery_double 
      : imageCount === 3 
      ? styles.portfolio_gallery_triple 
      : styles.portfolio_gallery_multiple

    if (imageCount === 1) {
      return (
        <div className={galleryClass}>
          <ImageWithBlurredBackground
            src={imageUrls[0] || "/placeholder.svg"}
            alt={name}
            className={styles.portfolio_gallery_image}
            onClick={handleImageClick(0)}
          />
        </div>
      )
    }

    if (imageCount === 2) {
      return (
        <div className={galleryClass}>
          <div className={styles.portfolio_gallery_item}>
            <ImageWithBlurredBackground
              src={imageUrls[0] || "/placeholder.svg"}
              alt={`${name} 1`}
              className={styles.portfolio_gallery_image}
              onClick={handleImageClick(0)}
            />
          </div>
          <div className={styles.portfolio_gallery_item}>
            <ImageWithBlurredBackground
              src={imageUrls[1] || "/placeholder.svg"}
              alt={`${name} 2`}
              className={styles.portfolio_gallery_image}
              onClick={handleImageClick(1)}
            />
          </div>
        </div>
      )
    }

    const showOverlay = imageCount > 3

    return (
      <div className={galleryClass}>
        <div className={styles.portfolio_gallery_main}>
          <ImageWithBlurredBackground
            src={imageUrls[0] || "/placeholder.svg"}
            alt={`${name} 1`}
            className={styles.portfolio_gallery_image}
            onClick={handleImageClick(0)}
          />
        </div>
        <div className={styles.portfolio_gallery_side}>
          <div className={styles.portfolio_gallery_item}>
            <ImageWithBlurredBackground
              src={imageUrls[1] || "/placeholder.svg"}
              alt={`${name} 2`}
              className={styles.portfolio_gallery_image}
              onClick={handleImageClick(1)}
            />
          </div>
          <div className={showOverlay ? styles.portfolio_gallery_item_overlay : styles.portfolio_gallery_item}>
            {showOverlay ? (
              <>
                <div className={styles.portfolio_image_container}>
                  <div
                    className={styles.portfolio_image_background}
                    style={{ backgroundImage: `url(${imageUrls[2] || "/placeholder.svg"})` }}
                  />
                  <img
                    src={imageUrls[2] || "/placeholder.svg"}
                    alt={`${name} 3`}
                    className={styles.portfolio_gallery_image}
                  />
                </div>
                <div className={styles.portfolio_gallery_overlay} onClick={handleImageClick(2)}>
                  <Plus size={24} />
                  <span>{imageCount - 3}</span>
                </div>
              </>
            ) : (
              <ImageWithBlurredBackground
                src={imageUrls[2] || "/placeholder.svg"}
                alt={`${name} 3`}
                className={styles.portfolio_gallery_image}
                onClick={handleImageClick(2)}
              />
            )}
          </div>
        </div>
      </div>
    )
  }


  return (
    <>
      <section className={styles.portfolioContainer}>
        <div
          className={`${styles.portfolio_card} ${isHovered ? styles.hovered : ""}`}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
          onClick={handleCardClick}
        >
          <div className={styles.portfolio_header}>
            <div className={styles.portfolio_header_badge} title={type === "producto" ? "Producto" : "Servicio"}>
              {type === "producto" ? <ProductIcon width={14} height={14} /> : <ServiceIcon width={14} height={14} />}
            </div>
          </div>
          <div className={styles.portfolio_hero}>
            {renderGallery()}
            <div className={styles.portfolio_badge_overlay} title={type === "producto" ? "Producto" : "Servicio"}>
              {type === "producto" ? <ProductIcon width={14} height={14} /> : <ServiceIcon width={14} height={14} />}
            </div>
            {precioFormateado && (
              <div className={styles.portfolio_price_overlay}>
                <span className={styles.portfolio_price}>{precioFormateado}</span>
              </div>
            )}
          </div>
          <div className={styles.portfolio_content}>
            <div className={styles.portfolio_content_header}>
              <h3 className={styles.portfolio_title}>{name}</h3>
              {hasImages && imageCount > 1 && (
                <div className={styles.portfolio_image_count}>
                  <ImageIcon size={14} />
                  <span>{imageCount}</span>
                </div>
              )}
            </div>

            {description && <p className={styles.portfolio_description}>{description}</p>}
            <div className={styles.portfolio_details}>
              {zone && zone.trim() !== "" && (
                <div className={styles.portfolio_detail}>
                  <MapPin size={14} />
                  <span>{zone}</span>
                </div>
              )}

              {(startDate || endDate) && (
                <div className={styles.portfolio_detail}>
                  <Calendar size={14} />
                  <span>
                    {fechaInicioFormateada}
                    {fechaInicioFormateada && fechaFinFormateada && " - "}
                    {fechaFinFormateada}
                  </span>
                </div>
              )}
            </div>
          </div>
          <div className={styles.portfolio_footer}>
            <div className={styles.portfolio_footer_profile}>
              <div
                className={styles.portfolio_footer_user}
                onClick={(e) => {
                  e.stopPropagation()
                  handleProfile(userId, completeName)
                }}
              >
                <div className={styles.portfolio_footer_avatar}>
                  {!imageError && profileImage ? (
                    <img
                      className={styles.portfolio_footer_avatar_img}
                      src={profileImage || "/placeholder.svg"}
                      alt="user"
                    />
                  ) : initials ? (
                    <div className={styles.portfolio_footer_avatar_initials}>{initials}</div>
                  ) : (
                    <div className={styles.portfolio_footer_avatar_img}>
                      <NoImage width={32} height={32} />
                    </div>
                  )}
                </div>
                <div className={styles.portfolio_footer_info}>
                  <p className={styles.portfolio_footer_name}>{completeName}</p>
                  <div className={styles.portfolio_footer_time}>
                    <MundoPost width={12} height={12} />
                    <span>
                      <DotPost height={8} width={8} />
                    </span>
                    {tiempoTranscurrido}
                  </div>
                </div>
              </div>
            </div>
            <div className={styles.portfolio_info}>
              <div className={styles.portfolio_info_item}>
                <Eye size={16} />
                <span>Ver detalles</span>
              </div>
            </div>
          </div>
        </div>
      </section>
      {showPortfolioDetail && (
        <PortfolioItemAttachment item={item} userData={userData} closeModal={() => setShowPortfolioDetail(false)} />
      )}
    </>
  )
}

const mapStateToProps = (state) => {
  return {
    userData: state.general.userData,
  }
}

export default connect(mapStateToProps, {})(PortfolioCard)
